import {
  Alert,
  Grid2 as Grid,
  Stack as MuiStack,
  Snackbar,
  SnackbarCloseReason,
  Typography,
  useTheme,
} from '@mui/material';
import { getStacksViewSx } from '@src/components/_shared/sxStyles/Config/stacks';
import TemplatesCard from '@src/components/Home/Config/ConfigurableModes/components/TemplatesCard';
import QueryKeys from '@src/constants/queryKeys';
import useStackMutations from '@src/hooks/api/mutations/useStackMutations';
import useStackValidation from '@src/hooks/useStackValidation';
import { ControlModes, Stack, StackEdited } from '@src/types/Stacks';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import ImportErrorDialog from '../ConfigurableModes/components/ImportErrorDialog';

function StackView({
  stack,
  isDefault,
}: {
  stack: Stack;
  isDefault?: boolean;
}) {
  const theme = useTheme();
  const stacksViewSx = getStacksViewSx(theme);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const { updateStack } = useStackMutations(stack?.id, isDefault);
  const queryClient = useQueryClient();
  const {
    validateTemplate,
    error: stackValidationError,
    resetError,
  } = useStackValidation();

  const handleUpdateStack = async (controlMode: ControlModes) => {
    const updatedStack = {
      name: stack.name,
      controlModes: stack.controlModes?.map((mode) => {
        if (mode.name === controlMode.name) {
          return controlMode;
        }
        return mode;
      }),
    };

    resetError();

    const isValid = await validateTemplate([updatedStack]);

    if (!isValid) {
      setErrorDialogOpen(true);
      return;
    }

    updateStack.mutate(updatedStack as StackEdited, {
      onSuccess: () => {
        if (isDefault) {
          queryClient.invalidateQueries({
            queryKey: [QueryKeys.defaultStack],
          });
        } else {
          queryClient.invalidateQueries({
            queryKey: [QueryKeys.stacks],
          });
        }
        setConfirmOpen(true);
      },
      onError: (error: Error) => {
        setError(error.message);
        setConfirmOpen(true);
      },
    });
  };

  const handleClose = (
    _: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') {
      return;
    }

    setConfirmOpen(false);
  };

  const handleErrorDialogClose = () => {
    setErrorDialogOpen(false);
    resetError();
  };

  return (
    <div>
      <ImportErrorDialog
        open={errorDialogOpen}
        onClose={handleErrorDialogClose}
        error={stackValidationError}
      />
      <Snackbar
        open={confirmOpen}
        autoHideDuration={5000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleClose}
          severity={error ? 'error' : 'success'}
          variant='filled'
        >
          {error ? error : 'Template updated successfully!'}
        </Alert>
      </Snackbar>
      <section>
        <MuiStack spacing={3}>
          <Grid container spacing={2}>
            <Grid size={4}>
              <Typography sx={stacksViewSx.view.titles.control}>
                Control Mode
              </Typography>
            </Grid>
            <Grid size={8}>
              <Typography sx={stacksViewSx.view.titles.stack}>
                {stack.name}
              </Typography>
            </Grid>
          </Grid>

          {stack.controlModes?.map((controlMode, index: number) => (
            <TemplatesCard
              key={index}
              controlMode={controlMode}
              handleUpdateStack={handleUpdateStack}
              isDefault={isDefault}
            />
          ))}
          {!stack.controlModes?.length && (
            <Typography sx={stacksViewSx.view.titles.noModes}>
              No modes to display
            </Typography>
          )}
        </MuiStack>
      </section>
    </div>
  );
}

export default StackView;
